// Utilitários para manipulação de cookies
import apiService from '../services/api';
import ptTranslations from '../locales/pt.json';
import enTranslations from '../locales/en.json';
import esTranslations from '../locales/es.json';
import frTranslations from '../locales/fr.json';
import zhTranslations from '../locales/zh.json';

// Mapa de traduções
const translationsMap = {
  pt: ptTranslations,
  en: enTranslations,
  es: esTranslations,
  fr: frTranslations,
  zh: zhTranslations
};

// Chaves para armazenamento local
export const STORAGE_KEYS = {
  COOKIE_CONSENT: 'conexao-saude-cookie-consent',
  COOKIE_PREFERENCES: 'conexao-saude-cookie-preferences',
  LANGUAGE_PREFERENCE: 'conexao-saude-language',
  REGION_PREFERENCE: 'conexao-saude-region'
};

// Função para obter preferência de idioma
export const getLanguagePreference = () => {
  return localStorage.getItem(STORAGE_KEYS.LANGUAGE_PREFERENCE) || 'pt';
};

// Função para obter traduções
const getTranslations = () => {
  const language = getLanguagePreference();
  return translationsMap[language] || ptTranslations;
};

// Função para obter configuração de cookies com traduções atualizadas
export const getTranslatedCookieConfig = () => {
  const t = getTranslations();

  return {
    [COOKIE_TYPES.NECESSARY]: {
      name: t.cookies.types.necessary.name,
      description: t.cookies.types.necessary.description,
      required: true,
      duration: t.cookies.durations.necessary
    },
    [COOKIE_TYPES.ANALYTICS]: {
      name: t.cookies.types.analytics.name,
      description: t.cookies.types.analytics.description,
      required: false,
      duration: t.cookies.durations.analytics
    },
    [COOKIE_TYPES.FUNCTIONAL]: {
      name: t.cookies.types.functional.name,
      description: t.cookies.types.functional.description,
      required: false,
      duration: t.cookies.durations.functional
    },
    [COOKIE_TYPES.MARKETING]: {
      name: t.cookies.types.marketing.name,
      description: t.cookies.types.marketing.description,
      required: false,
      duration: t.cookies.durations.marketing
    }
  };
};

// Gerador de session ID único
const generateSessionId = () => {
  return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
};

// Obter ou criar session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('conexao-saude-session-id');
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem('conexao-saude-session-id', sessionId);
  }
  return sessionId;
};

// Tipos de cookies permitidos
export const COOKIE_TYPES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  FUNCTIONAL: 'functional',
  MARKETING: 'marketing'
};

// Configuração de cookies por tipo (com traduções)
export const getCookieConfig = () => {
  return getTranslatedCookieConfig();
};

// Manter compatibilidade com código existente (usa traduções em português por padrão)
export const COOKIE_CONFIG = getTranslatedCookieConfig();

// Função para definir cookie
export const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

// Função para obter cookie
export const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

// Função para remover cookie
export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// Função para verificar se o consentimento foi dado
export const hasCookieConsent = () => {
  return localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT) === 'true';
};

// Função para salvar consentimento
export const saveCookieConsent = async (preferences) => {
  const sessionId = getSessionId();
  const consentGranted = Object.values(preferences).some(value => value);

  // Salvar localmente primeiro
  localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, consentGranted.toString());
  localStorage.setItem(STORAGE_KEYS.COOKIE_PREFERENCES, JSON.stringify(preferences));

  // Definir cookies baseados nas preferências
  Object.keys(preferences).forEach(type => {
    if (preferences[type] || COOKIE_CONFIG[type]?.required) {
      setCookie(`cookie-consent-${type}`, 'true', type === COOKIE_TYPES.ANALYTICS ? 730 : 365);
    }
  });

  // Enviar para API
  try {
    const apiData = {
      session_id: sessionId,
      consent_granted: consentGranted,
      cookie_preferences: {
        necessarios: preferences[COOKIE_TYPES.NECESSARY] || false,
        analiticos: preferences[COOKIE_TYPES.ANALYTICS] || false,
        funcionais: preferences[COOKIE_TYPES.FUNCTIONAL] || false,
        marketing: preferences[COOKIE_TYPES.MARKETING] || false
      }
    };

    await apiService.saveCookieConsent(apiData);
  } catch (error) {
    const t = getTranslations();
    console.error(t.cookies.messages.apiError, error);
    // Continuar funcionando mesmo se a API falhar
  }
};

// Função para obter preferências salvas
export const getCookiePreferences = () => {
  const saved = localStorage.getItem(STORAGE_KEYS.COOKIE_PREFERENCES);
  if (saved) {
    return JSON.parse(saved);
  }

  // Preferências padrão
  return {
    [COOKIE_TYPES.NECESSARY]: true,
    [COOKIE_TYPES.ANALYTICS]: true,
    [COOKIE_TYPES.FUNCTIONAL]: true,
    [COOKIE_TYPES.MARKETING]: false
  };
};

// Função para salvar preferências de idioma
export const saveLanguagePreference = (language) => {
  localStorage.setItem(STORAGE_KEYS.LANGUAGE_PREFERENCE, language);
  if (getCookiePreferences()[COOKIE_TYPES.FUNCTIONAL]) {
    setCookie('preferred-language', language, 365);
  }
};

// Função para salvar preferências de região
export const saveRegionPreference = (region) => {
  localStorage.setItem(STORAGE_KEYS.REGION_PREFERENCE, region);
  if (getCookiePreferences()[COOKIE_TYPES.FUNCTIONAL]) {
    setCookie('preferred-region', region, 365);
  }
};

// Função para obter preferência de região
export const getRegionPreference = () => {
  return localStorage.getItem(STORAGE_KEYS.REGION_PREFERENCE) || 'br';
};

// Função para rastrear evento de analytics
export const trackEvent = async (eventName, eventData = {}) => {
  const preferences = getCookiePreferences();
  if (preferences[COOKIE_TYPES.ANALYTICS]) {

    // Dados para analytics
    const analyticsData = {
      timestamp: new Date().toISOString(),
      event: eventName,
      data: eventData,
      page: window.location.pathname,
      userAgent: navigator.userAgent
    };

    // Armazenar localmente para demonstração
    const existingEvents = JSON.parse(localStorage.getItem('analytics-events') || '[]');
    existingEvents.push(analyticsData);
    localStorage.setItem('analytics-events', JSON.stringify(existingEvents.slice(-100)));

    // Enviar para API
    try {
      const sessionId = getSessionId();

      // Obter estatísticas existentes para link_clicks e check_history
      const existingEvents = JSON.parse(localStorage.getItem('analytics-events') || '[]');
      const linkClicks = existingEvents.filter(e => e.event === 'link_click');
      const pageViews = existingEvents.filter(e => e.event === 'page_view');

      // Validar e limpar dados antes de enviar
      const apiData = {
        activity_type: eventName || 'page_view',
        page: window.location.pathname || '/',
        link: eventData?.url || eventData?.link || null,
        link_clicks: linkClicks.length,
        check_history: pageViews.length
      };

      // Remover campos null ou undefined
      const cleanApiData = {};
      Object.keys(apiData).forEach(key => {
        if (apiData[key] !== null && apiData[key] !== undefined) {
          cleanApiData[key] = apiData[key];
        }
      });

      await apiService.registerUserActivity(sessionId, cleanApiData);
    } catch (error) {
      const t = getTranslations();
      console.error(t.cookies.messages.trackingError, error);
      // Continuar funcionando mesmo se a API falhar
    }
  } else {
    const t = getTranslations();
    console.log(t.cookies.messages.analyticsDisabled, eventName);
  }
};

// Função para rastrear visualização de página
export const trackPageView = (pageName) => {
  // Validar se pageName é válido
  const validPageName = pageName && typeof pageName === 'string' ? pageName : window.location.pathname || '/';
  trackEvent('page_view', { page: validPageName });
};

// Função para rastrear clique em link
export const trackLinkClick = (linkText, linkUrl) => {
  // Validar dados antes de enviar
  const validText = linkText && typeof linkText === 'string' ? linkText : 'Unknown Link';
  const validUrl = linkUrl && typeof linkUrl === 'string' ? linkUrl : null;

  trackEvent('link_click', {
    text: validText,
    url: validUrl,
    link: validUrl // Fallback para compatibilidade
  });
};

// Função para obter estatísticas básicas
export const getBasicStats = () => {
  const events = JSON.parse(localStorage.getItem('analytics-events') || '[]');
  const pageViews = events.filter(e => e.event === 'page_view');
  const linkClicks = events.filter(e => e.event === 'link_click');

  return {
    totalPageViews: pageViews.length,
    totalLinkClicks: linkClicks.length,
    uniquePages: [...new Set(pageViews.map(e => e.data.page))].length,
    lastActivity: events.length > 0 ? events[events.length - 1].timestamp : null
  };
};
